/**
 * CloudWatch RUM, loaded only once a signed-in user's page records usage (design D3, D4): signed with the
 * signed-in user's Cognito credentials, no cookies, no fetch telemetry, screen views recorded by the app.
 */
export const loadRumClient = async (options) => {
    const [{ AwsRum }, { fromCognitoIdentityPool }] = await Promise.all([
        import("aws-rum-web"),
        import("@aws-sdk/credential-provider-cognito-identity"),
    ]);
    const credentialsFor = (sessionToken) => fromCognitoIdentityPool({
        clientConfig: { region: options.region },
        identityPoolId: options.identityPoolId,
        logins: { [options.loginKey]: sessionToken },
    });
    // The request a flush starts goes out with keepalive, so it outlives the page that is being left.
    let flushSent;
    const fetchFunction = (input, init) => {
        const sent = flushSent;
        flushSent = undefined;
        try {
            return fetch(sent && input instanceof Request ? new Request(input, { keepalive: true }) : input, init);
        }
        finally {
            sent?.();
        }
    };
    const rum = new AwsRum(options.appMonitorId, "0", options.region, {
        allowCookies: false,
        sessionSampleRate: 1,
        telemetries: ["errors", "performance"],
        disableAutoPageView: true,
        enableXRay: false,
        endpoint: `https://dataplane.rum.${options.region}.amazonaws.com`,
        sessionAttributes: options.attributes,
        // A long editing session must not run out: the default stops recording after 200 events.
        sessionEventLimit: 0,
        // Resource timings keep their file type but not their URL: a request URL can carry what a user typed.
        recordResourceUrl: false,
        fetchFunction,
    });
    rum.setAwsCredentials(credentialsFor(options.sessionToken));
    return {
        recordEvent: (type, data) => rum.recordEvent(type, data),
        recordPageView: (page) => rum.recordPageView(page),
        recordError: (error) => rum.recordError(error),
        addSessionAttributes: (attributes) => rum.addSessionAttributes(attributes),
        setSessionToken: (sessionToken) => rum.setAwsCredentials(credentialsFor(sessionToken)),
        disable: () => rum.disable(),
        enable: () => rum.enable(),
        startSession: () => void rum.startSession(),
        // Settles when the request is sent. With nothing to send there is no request, and the caller's wait ends it.
        flush: () => new Promise((resolve) => {
            flushSent?.();
            flushSent = resolve;
            rum.dispatch();
        }),
    };
};
//# sourceMappingURL=rumClient.js.map